import { TableRow } from "@mui/material";
import { constants } from "../lib/Variable";
import CryptoJS from "crypto-js";
import React from "react";
import { View, Text } from "@react-pdf/renderer";
const encryptionKey = "abcdefghijklmnopqrstuvwxyz123456";

const handleOpenSnackBar = (type, message) => {
  constants.snackRef.isOpenSnack({
    snackType: type,
    snackMessage: message,
  });
};

// Encryption function
const encryptPassword = (password) => {
  const encrypted = CryptoJS.AES.encrypt(password, encryptionKey).toString();
  return encrypted;
};

// Decryption function
const decryptPassword = (encryptedPassword) => {
  const bytes = CryptoJS.AES.decrypt(encryptedPassword, encryptionKey);
  const decrypted = bytes.toString(CryptoJS.enc.Utf8);
  return decrypted;
};

const handleApiCall = async (
  id,
  apiFunction,
  payload,
  setIsLoading,
  handleClose,
  getAllData,
) => {
  try {
    setIsLoading(true);
    const response = await apiFunction(id, payload);
    setIsLoading(false);
    if (response?.data.status == "success") {
      handleOpenSnackBar("success", response?.data?.message);
      handleClose();
      getAllData();
    } else {
      handleOpenSnackBar("error", response?.data?.message);
    }
  } catch (error) {
    handleOpenSnackBar("error", "Something went wrong");
    setIsLoading(false);
  }
};

function getTotalSalePrice(data) {
  return data.reduce((totalSum, obj) => {
    // when legacyTariff is selected and additionalService is NOT selected

    // if (obj.upgradeCharge.isSelected) {
    //   return totalSum + obj.upgradeCharge.value;
    // }

    if (
      obj.legacyTariff.isSelected === true &&
      obj.additionalService.isSelected === false
    ) {
      const lineValuesSum = obj.lineValues.reduce((sum, line) => {
        return sum + (line?.legacy?.salePrice || 0);
      }, 0);
      return totalSum + lineValuesSum;
    }

    // when digitalTariff is selected and additionalService is NOT selected
    if (
      obj.digitalTariff.isSelected === true &&
      obj.additionalService.isSelected === false
    ) {
      const lineValuesSum = obj.lineValues.reduce((sum, line) => {
        return sum + (line?.ngnValue?.salePrice || 0);
      }, 0);
      return totalSum + lineValuesSum;
    }

    // when legacyTariff and additionalService BOTH are selected
    if (
      obj.legacyTariff.isSelected === true &&
      obj.additionalService.isSelected === true
    ) {
      const uniqueCareLevels = new Set(); // To track care levels that should only be counted once
      let additionalServiceSum = 0; // Sum of additional service prices

      obj.lineValues.forEach((line) => {
        // Check for legacy services and add prices accordingly
        [
          "legacyService1",
          "legacyService2",
          "legacyService3",
          "legacyService4",
        ].forEach((serviceKey) => {
          const service = line[serviceKey];
          if (
            [
              "Care Level 2",
              "Care Level 3",
              "Care Level 4",
              "Static IP",
              "Business Assurance",
            ].includes(service?.value)
          ) {
            // Add care level price only once
            if (!uniqueCareLevels.has(service?.value)) {
              uniqueCareLevels.add(service?.value);
              additionalServiceSum += service?.salePrice || 0;
            }
          } else if (service?.salePrice) {
            // Add non-care-level service prices individually
            additionalServiceSum += service?.salePrice || 0;
          }
        });
      });

      // Sum legacy tariff sale price with additional services
      const lineValuesSum = obj.lineValues.reduce((sum, line) => {
        return sum + (line?.legacy?.salePrice || 0);
      }, 0);

      return totalSum + lineValuesSum + additionalServiceSum;
    }

    // when digitalTariff and additionalService BOTH are selected
    if (
      obj.digitalTariff.isSelected === true &&
      obj.additionalService.isSelected === true
    ) {
      const uniqueCareLevels = new Set(); // To track care levels that should only be counted once
      let additionalServiceSum = 0; // Sum of additional service prices

      obj.lineValues.forEach((line) => {
        // Check for digital services and add prices accordingly
        ["ngnService1", "ngnService2", "ngnService3", "ngnService4"].forEach(
          (serviceKey) => {
            const service = line[serviceKey];
            if (
              [
                "Care Level 2",
                "Care Level 3",
                "Care Level 4",
                "Static IP",
                "Business Assurance",
              ].includes(service?.value)
            ) {
              // Add care level price only once
              if (!uniqueCareLevels.has(service?.value)) {
                uniqueCareLevels.add(service?.value);
                additionalServiceSum += service?.salePrice || 0;
              }
            } else if (service?.salePrice) {
              // Add non-care-level service prices individually
              additionalServiceSum += service?.salePrice;
            }
          },
        );
      });

      // Sum digital tariff sale price with additional services
      const lineValuesSum = obj.lineValues.reduce((sum, line) => {
        return sum + (line?.ngnValue?.salePrice || 0);
      }, 0);

      return totalSum + lineValuesSum + additionalServiceSum;
    }

    return totalSum; // Default return if no conditions match
  }, 0);
}

function getTotalBuyPrice(data) {
  return data.reduce((totalSum, obj) => {
    // when legacyTariff is selected and additionalService is NOT selected
    if (
      obj.legacyTariff.isSelected === true &&
      obj.additionalService.isSelected === false
    ) {
      const lineValuesSum = obj.lineValues.reduce((sum, line) => {
        return sum + (line?.legacy?.buyPrice || 0);
      }, 0);
      return totalSum + lineValuesSum;
    }

    // when digitalTariff is selected and additionalService is NOT selected
    if (
      obj.digitalTariff.isSelected === true &&
      obj.additionalService.isSelected === false
    ) {
      const lineValuesSum = obj.lineValues.reduce((sum, line) => {
        return sum + (line?.ngnValue?.buyPrice || 0);
      }, 0);
      return totalSum + lineValuesSum;
    }

    // when legacyTariff and additionalService BOTH are selected
    if (
      obj.legacyTariff.isSelected === true &&
      obj.additionalService.isSelected === true
    ) {
      const uniqueCareLevels = new Set(); // To track care levels that should only be counted once
      let additionalServiceSum = 0; // Sum of additional service prices

      obj.lineValues.forEach((line) => {
        // Check for legacy services and add prices accordingly
        [
          "legacyService1",
          "legacyService2",
          "legacyService3",
          "legacyService4",
        ].forEach((serviceKey) => {
          const service = line[serviceKey];
          if (
            [
              "Care Level 2",
              "Care Level 3",
              "Care Level 4",
              "Static IP",
              "Business Assurance",
            ].includes(service?.value)
          ) {
            // Add care level price only once
            if (!uniqueCareLevels.has(service?.value)) {
              uniqueCareLevels.add(service?.value);
              additionalServiceSum += service?.buyPrice || 0;
            }
          } else if (service?.buyPrice) {
            // Add non-care-level service prices individually
            additionalServiceSum += service?.buyPrice || 0;
          }
        });
      });

      // Sum legacy tariff sale price with additional services
      const lineValuesSum = obj.lineValues.reduce((sum, line) => {
        return sum + (line?.legacy?.buyPrice || 0);
      }, 0);

      return totalSum + lineValuesSum + additionalServiceSum;
    }

    // when digitalTariff and additionalService BOTH are selected
    if (
      obj.digitalTariff.isSelected === true &&
      obj.additionalService.isSelected === true
    ) {
      const uniqueCareLevels = new Set(); // To track care levels that should only be counted once
      let additionalServiceSum = 0; // Sum of additional service prices

      obj.lineValues.forEach((line) => {
        // Check for digital services and add prices accordingly
        ["ngnService1", "ngnService2", "ngnService3", "ngnService4"].forEach(
          (serviceKey) => {
            const service = line[serviceKey];
            if (
              [
                "Care Level 2",
                "Care Level 3",
                "Care Level 4",
                "Static IP",
                "Business Assurance",
              ].includes(service?.value)
            ) {
              // Add care level price only once
              if (!uniqueCareLevels.has(service?.value)) {
                uniqueCareLevels.add(service?.value);
                additionalServiceSum += service?.buyPrice || 0;
              }
            } else if (service?.buyPrice) {
              // Add non-care-level service prices individually
              additionalServiceSum += service?.buyPrice;
            }
          },
        );
      });

      // Sum digital tariff sale price with additional services
      const lineValuesSum = obj.lineValues.reduce((sum, line) => {
        return sum + (line?.ngnValue?.buyPrice || 0);
      }, 0);

      return totalSum + lineValuesSum + additionalServiceSum;
    }

    return totalSum; // Default return if no conditions match
  }, 0);
}

function getTotalTariffCharges(data) {
  return data.reduce((totalSum, obj) => {
    // when legacyTariff is selected and additionalService is NOT selected
    if (obj.legacyTariff.isSelected === true) {
      const lineValuesSum = obj.lineValues.reduce((sum, line) => {
        return sum + (line?.legacy?.salePrice || 0);
      }, 0);
      return totalSum + lineValuesSum;
    }

    // when digitalTariff is selected and additionalService is NOT selected
    if (obj.digitalTariff.isSelected === true) {
      const lineValuesSum = obj.lineValues.reduce((sum, line) => {
        return sum + (line?.ngnValue?.salePrice || 0);
      }, 0);
      return totalSum + lineValuesSum;
    }

    return totalSum; // Default return if no conditions match
  }, 0);
}

function getTotalServiceChargeOnTarrif(data) {
  return data.reduce((totalSum, obj) => {
    // when legacyTariff additionalService  are selected
    if (
      obj.legacyTariff.isSelected === true &&
      obj.additionalService.isSelected === true
    ) {
      const uniqueCareLevels = new Set(); // To track care levels that should only be counted once
      let additionalServiceSum = 0; // Sum of additional service prices

      obj.lineValues.forEach((line) => {
        // Check for legacy services and add prices accordingly
        [
          "legacyService1",
          "legacyService2",
          "legacyService3",
          "legacyService4",
        ].forEach((serviceKey) => {
          const service = line[serviceKey];
          if (
            [
              "Care Level 2",
              "Care Level 3",
              "Care Level 4",
              "Static IP",
              "Business Assurance",
            ].includes(service?.value)
          ) {
            // Add care level price only once
            if (!uniqueCareLevels.has(service?.value)) {
              uniqueCareLevels.add(service?.value);
              additionalServiceSum += service?.salePrice || 0;
            }
          } else if (service?.salePrice) {
            // Add non-care-level service prices individually
            additionalServiceSum += service?.salePrice || 0;
          }
        });
      });

      return totalSum + additionalServiceSum;
    }

    if (
      obj.digitalTariff.isSelected === true &&
      obj.additionalService.isSelected === true
    ) {
      const uniqueCareLevels = new Set(); // To track care levels that should only be counted once
      let additionalServiceSum = 0; // Sum of additional service prices

      obj.lineValues.forEach((line) => {
        // Check for digital services and add prices accordingly
        ["ngnService1", "ngnService2", "ngnService3", "ngnService4"].forEach(
          (serviceKey) => {
            const service = line[serviceKey];
            if (
              [
                "Care Level 2",
                "Care Level 3",
                "Care Level 4",
                "Static IP",
                "Business Assurance",
              ].includes(service?.value)
            ) {
              // Add care level price only once
              if (!uniqueCareLevels.has(service?.value)) {
                uniqueCareLevels.add(service?.value);
                additionalServiceSum += service?.salePrice || 0;
              }
            } else if (service?.salePrice) {
              // Add non-care-level service prices individually
              additionalServiceSum += service?.salePrice;
            }
          },
        );
      });

      return totalSum + additionalServiceSum;
    }

    return totalSum; // Default return if no conditions match
  }, 0);
}

const calculateUpgradeCharge = (data) => {
  let total = {
    salePrice: 0,
    buyPrice: 0,
    whole: 0,
  };
  data.reduce((acc, obj) => {
    if (obj.upgradeCharge.isSelected) {
      total.whole += 60;
      total.salePrice += parseFloat(obj.upgradeCharge.salePrice);
      total.buyPrice +=
        obj.upgradeCharge.salePrice - obj.upgradeCharge.buyPrice;
    }
    if (obj.migration?.isSelected) {
      total.whole += 60;
      total.salePrice += parseFloat(obj.migration?.salePrice || 0);
      total.buyPrice += obj.migration?.salePrice || 0 - obj.migration?.buyPrice;
    }
  }, total);
  return total;
};

const formatObjKey = (key) => {
  // Convert camelCase or snake_case to Title Case with spaces
  return key
    .replace(/([A-Z])/g, " $1") // Add space before capital letters
    .replace(/([a-z])([A-Z])/g, "$1 $2") // Add space before capital letters that follow lowercase letters
    .replace(/([a-zA-Z])([0-9])/g, "$1 $2") // Add space before numbers that follow letters
    .replace(/([0-9])([a-zA-Z])/g, "$1 $2") // Add space before letters that follow numbers
    .toLowerCase() // Convert to lowercase
    .replace(/(^|\s)\S/g, (letter) => letter.toUpperCase()); // Capitalize first letter of each word
};

const calcultePackageAmount = (arg) => {
  let total = 0;
  arg?.lineValues?.map((a) => {
    total += a.legacy.salePrice;
  });
  return total.toFixed(2);
};

const contractTerm = (arg) => {
  let month = "";
  arg?.map((a) => {
    if (a.isSelected) month = a.title;
  });
  return month;
};

const upgradeChargeAmount = (arg) => {
  let total = 0;
  arg.map((a) => {
    if (a.isSelected === true) {
      total = a.price;
    }
  });
  // return total !== "Not required" ? `£${total}` : total;
  return total;
};

function routerTotalPrice(value, price) {
  return value * price;
}

function checkIfPaygExists(data) {
  return data.some((item) =>
    item?.lineValues?.some(
      (line) =>
        line?.legacy?.other?.UkLandlineCalls === "PAYG" ||
        line?.ngnValue?.other?.UkLandlineCalls === "PAYG",
    ),
  );
}

function calculateRevenue(sale, buy) {
  let gross = sale - buy;
  let profitPercentage = (gross / buy) * 100;

  return {
    revenue: sale,
    gross: gross.toFixed(2),
    profitPercentage: profitPercentage.toFixed(2),
  };
}

const checkOnlyDigitalTarrifSelected = (params) => {
  return params.some((val) => {
    if (val.legacyTariff.isSelected) {
      return true;
    } else {
      return false;
    }
  });
};

// Check any of the connection box has platform upfrade charge selected
const checkPlatformUpradeSelected = (params) => {
  return params.some((val) => {
    if (val.upgradeCharge.isSelected) {
      return true;
    }
    if (val?.migration?.isSelected) {
      // ye yad rkhna hai
      return true;
    } else {
      return false;
    }
  });
};

// function areAllKeysFilled(obj, ignoreKeys = []) {
//   return Object.entries(obj).every(
//     ([key, value]) =>
//       ignoreKeys.includes(key) ||
//       (value !== null && value !== undefined && value !== "")
//   );
// }

function areAllKeysFilled(obj, ignoreKeys = []) {
  const keys = {
    currentProvider: "Current Provider",
    title: "Title",
    name: "Customer Name",
    designation: "Designation",
    email: "Customer",
    mobileNo: "Mobile Number",
    businessName: "Business Name",
    businessType: "Business Type",
    address1: "1st Line Address",
    city: "City",
    postCode: "Post Code",
    billingEmail: "Billing Email",
  };

  let emptyFields = [];

  Object.entries(obj).forEach(([key, value]) => {
    if (
      !ignoreKeys.includes(key) &&
      (value === null || value === undefined || value === "")
    ) {
      emptyFields.push(keys[key] || key);
    }
  });

  return { allFilled: emptyFields.length === 0, emptyFields };
}
/**
 * Finds the selected contract length from an array of lengths.
 *
 * @param {Array} length - An array of contract lengths, where each length object contains an `isSelected` property.
 * @returns {Object} - The length object where `isSelected` is true, or `undefined` if no length is selected.
 */
const selectedContractLength = (length) => {
  const selected = length.find((x) => x.isSelected === true);
  return selected;
};

/** Buy-pricing contracts (e.g. Saver) — do not rely on title; use pricingMode. */
const isBuyPricingContract = (length) => {
  return selectedContractLength(length)?.pricingMode === "buy";
};

/** Dropdown display price: buyPrice when buy mode, else salePrice. */
const getTariffListPrice = (tariff, length) => {
  if (!tariff) return "";
  return isBuyPricingContract(length) ? tariff.buyPrice : tariff.salePrice;
};

/** On tariff select in buy mode, seed editable salePrice from buyPrice. */
const withContractPricing = (tariff, length) => {
  if (!tariff) return tariff;
  if (isBuyPricingContract(length)) {
    return { ...tariff, salePrice: tariff.buyPrice };
  }
  return tariff;
};

/**
 * Checks if any of the line values in the `cliData` array contain an error.
 *
 * This function iterates through the `cliData` array and checks each `lineValues` array
 * to see if any of its objects have a property with an `error` set to `true`.
 *
 * @param {Array} data - The array of CLI data that contains line values and services.
 * @returns {boolean} - Returns `true` if any `lineValue` has an error, otherwise `false`.
 */

const hasErrorInLineValues = (data) => {
  return data.some((item) =>
    item.lineValues.some((lineValue) =>
      Object.values(lineValue).some(
        (service) => service && service.error === true,
      ),
    ),
  );
};

/**
 * Converts NaN values to 0, and returns other values as is.
 *
 * @param {*} value - The value to check and possibly convert.
 * @returns {*} - Returns 0 if the value is NaN, otherwise returns the original value.
 */
const convertNaNToZero = (value) => {
  return isNaN(value) ? 0 : value;
};

function handleCheckAllDataFill(params) {
  let hasValidData = false;
  hasValidData = params?.some((val) => {
    // Check for legacyTariff and legacy services
    if (val.legacyTariff.isSelected) {
      // Check for a valid legacy value in lineValues
      const hasLegacy = val.lineValues?.some((item) => item.legacy !== null);
      if (hasLegacy) return true;

      // Check for valid legacy services if additionalService is selected
      if (val.additionalService.isSelected) {
        const hasLegacyService = val.lineValues?.some((item) => {
          return (
            item.legacyService1 !== null ||
            item.legacyService2 !== null ||
            item.legacyService3 !== null ||
            item.legacyService4 !== null
          );
        });
        if (hasLegacyService) return true;
      }
    }

    // Check for digitalTariff and digital services (ngnValue and ngnServices)
    if (val.digitalTariff.isSelected) {
      // Check for a valid ngnValue in lineValues
      const hasNgnValue = val.lineValues?.some(
        (item) => item.ngnValue !== null,
      );
      if (hasNgnValue) return true;

      // Check for valid ngnServices if additionalService is selected
      if (val.additionalService.isSelected) {
        const hasNgnService = val.lineValues?.some((item) => {
          return (
            item.ngnService1 !== null ||
            item.ngnService2 !== null ||
            item.ngnService3 !== null ||
            item.ngnService4 !== null
          );
        });
        if (hasNgnService) return true;
      }
    }

    //  check if upgradeCharge is selected
    if (val.upgradeCharge.isSelected) return true;

    // check if mobile number has some values
    if (val.mobileNo) return true;

    if (val.differentAddress.isSelected && val.differentAddress.value)
      return true;

    return false;
  });

  // if connection box has more than one values
  // if (params.length > 1) return (hasValidData = true);

  return hasValidData;
}

// contract pdf functions to render table row

/**
 *
 * @param {Array} services
 * @param {Number to Multiply} mul
 * @returns table row to show additional service on different conditions
 */

const renderAdditionalService = (services, mul) => {
  // Check service contain values
  let availableService = services.filter((y) => y !== null);
  // let iServiceSelected = services?.some((cccc) => cccc?.value !== "");

  let serviceKey = services?.map((cccc, _ind) => {
    if (cccc?.value) {
      return (
        <span>
          {`${cccc.value} ${_ind < availableService?.length - 1 ? `/` : ``} `}{" "}
        </span>
      );
    }
  });

  let serviceValue = services?.map((cccc, _ind) => {
    let multpliedBy = cccc?.salePrice;
    let isCareLevelIncluded = ![
      "Care Level 4",
      "Care Level 3",
      "Care Level 2",
      "Static IP",
      "Business Assurance",
    ].includes(cccc?.value);

    if (isCareLevelIncluded) {
      multpliedBy = multpliedBy * mul;
    }

    if (cccc?.salePrice) {
      return (
        <span>
          {`£${formatToTwoDecimals(multpliedBy || cccc.salePrice)} ${
            _ind < availableService?.length - 1 ? `/` : ``
          } `}{" "}
        </span>
      );
    }
  });

  if (availableService?.length > 0) {
    return (
      <TableRow>
        <td
          className="table-cell"
          style={{ borderRight: "2px solid #c3c1c1", width: "50%" }}
        >
          {serviceKey}
        </td>
        <td
          className="table-cell"
          style={{ width: "50%", textAlign: "center" }}
        >
          {serviceValue}
        </td>
      </TableRow>
    );
  }
};

const renderAdditionalServiceForContractPdf = (services, mul) => {
  // Check service contain values
  let availableService = services.filter((y) => y !== null);
  // let iServiceSelected = services?.some((cccc) => cccc?.value !== "");

  let serviceKey = services?.map((cccc, _ind) => {
    if (cccc?.value) {
      return (
        <Text>
          {`${cccc.value} ${_ind < availableService?.length - 1 ? `/` : ``} `}{" "}
        </Text>
      );
    }
  });

  let serviceValue = services?.map((cccc, _ind) => {
    let multpliedBy = cccc?.salePrice;
    let isCareLevelIncluded = ![
      "Care Level 4",
      "Care Level 3",
      "Care Level 2",
      "Static IP",
      "Business Assurance",
    ].includes(cccc?.value);

    if (isCareLevelIncluded) {
      multpliedBy = multpliedBy * mul;
    }

    if (cccc?.salePrice) {
      return (
        <Text>
          {`£${formatToTwoDecimals(multpliedBy || cccc.salePrice)} ${
            _ind < availableService?.length - 1 ? `/` : ``
          } `}{" "}
        </Text>
      );
    }
  });

  if (availableService?.length > 0) {
    return (
      <View
        style={{
          flexDirection: "row",
          borderBottom: "2px solid #c3c1c1",
        }}
      >
        <Text
          style={{
            width: "50%",
            padding: 6,
            fontSize: 11,
            fontWeight: "light",
            borderRight: "2px solid #c3c1c1",
          }}
        >
          {serviceKey}
        </Text>
        <Text
          style={{
            width: "50%",
            padding: 6,
            fontSize: 11,
            fontWeight: "light",
            textAlign: "center",
          }}
        >
          {serviceValue}
        </Text>
      </View>
    );
  }
};

/**
 *
 * @param {Selected service detail} details
 * @returns render table row show key along its value on table
 */

const renderKeyPairsRows = (details) => {
  return Object.entries(details).map(([key, value]) => {
    if (value !== "NA")
      return (
        <TableRow key={key}>
          <td
            className="table-cell"
            style={{ borderRight: "2px solid #c3c1c1", width: "50%" }}
          >
            {/* {formatObjKey(key)} */}
            {key}
          </td>
          <td
            className="table-cell"
            style={{ width: "50%", textAlign: "center" }}
          >
            {value}
          </td>
        </TableRow>
      );
  });
};

const renderKeyPairsRowsForContractPdf = (details) => {
  return Object.entries(details).map(([key, value]) => {
    if (value !== "NA")
      return (
        <View
          style={{
            flexDirection: "row",
            borderBottom: "2px solid #c3c1c1",
          }}
        >
          <Text
            style={{
              width: "50%",
              padding: 6,
              fontSize: 11,
              fontWeight: "light",
              borderRight: "2px solid #c3c1c1",
            }}
          >
            {key}
          </Text>
          <Text
            style={{
              width: "50%",
              padding: 6,
              fontSize: 11,
              fontWeight: "light",
              textAlign: "center",
            }}
          >
            {value}
          </Text>
        </View>
      );
  });
};

/**
 * if option of upgrade charge selected on each connection box it will show
 * @param {upgradeCharge key} option
 * @returns show platform upgrade charge on each connection portion
 */

const renderSogeaCharges = (option) => {
  if (option.isSelected) {
    return (
      <TableRow>
        <td
          className="table-cell"
          style={{ borderRight: "2px solid #c3c1c1", width: "50%" }}
        >
          Platform Upgrade Charge
        </td>
        <td
          className="table-cell"
          style={{ width: "50%", textAlign: "center" }}
        >
          £{option.salePrice}
        </td>
      </TableRow>
    );
  }
};

const renderAddHocServices = (isReqDebitDetail, generalInfo) => {
  if (isReqDebitDetail.no || generalInfo.eBilling == "No") {
    return (
      <>
        {generalInfo.eBilling == "No" && (
          <TableRow>
            <td
              className="table-cell"
              style={{ borderRight: "2px solid #c3c1c1", width: "50%" }}
            >
              Paper Billing Charges
            </td>
            <td
              className="table-cell"
              style={{ width: "50%", textAlign: "center" }}
            >
              £5
            </td>
          </TableRow>
        )}
        {isReqDebitDetail.no && (
          <TableRow>
            <td
              className="table-cell"
              style={{ borderRight: "2px solid #c3c1c1", width: "50%" }}
            >
              Payment Processing Fee
            </td>
            <td
              className="table-cell"
              style={{ width: "50%", textAlign: "center" }}
            >
              £7
            </td>
          </TableRow>
        )}
      </>
    );
  }
};

// RenderNgnTable

const renderTableDetailsForLegacy = (arg, _, contractLength) => {
  const services = [
    arg?.lineValues[0]?.legacyService1,
    arg?.lineValues[0]?.legacyService2,
    arg?.lineValues[0]?.legacyService3,
    arg?.lineValues[0]?.legacyService4,
  ];

  return arg?.lineValues.slice(0, 1).map((item, index) => {
    return (
      <React.Fragment key={index}>
        {renderKeyPairsRows(item.legacy.other)}
        {renderSogeaCharges(arg.upgradeCharge)}
        <TableRow>
          <td
            className="table-cell"
            style={{ borderRight: "2px solid #c3c1c1", width: "50%" }}
          >
            Contract Term
          </td>
          <td
            className="table-cell"
            style={{ width: "50%", textAlign: "center" }}
          >
            {contractLength.title}
          </td>
        </TableRow>
        <TableRow>
          <td
            className="table-cell"
            style={{ borderRight: "2px solid #c3c1c1", width: "50%" }}
          >
            Tariff Monthly Rental
          </td>
          <td
            className="table-cell"
            style={{ width: "50%", textAlign: "center" }}
          >
            £{calcultePackageAmount(arg)} per month
          </td>
        </TableRow>

        {arg?.additionalService.isSelected &&
          renderAdditionalService(services, arg?.lineValues?.length)}
      </React.Fragment>
    );
  });
};

const renderTableDetailsForNgN = (mainOption, arg, _, contractLength) => {
  const services = [
    arg?.ngnService1,
    arg?.ngnService2,
    arg?.ngnService3,
    arg?.ngnService4,
  ];

  return (
    <React.Fragment>
      {renderKeyPairsRows(arg?.ngnValue?.other)}
      {renderSogeaCharges(mainOption.upgradeCharge)}

      <TableRow>
        <td
          className="table-cell"
          style={{ borderRight: "2px solid #c3c1c1", width: "50%" }}
        >
          Contract Term
        </td>
        <td
          className="table-cell"
          style={{ width: "50%", textAlign: "center" }}
        >
          {contractLength.title}
        </td>
      </TableRow>
      <TableRow>
        <td
          className="table-cell"
          style={{ borderRight: "2px solid #c3c1c1", width: "50%" }}
        >
          Tariff Monthly Rental
        </td>
        <td
          className="table-cell"
          style={{ width: "50%", textAlign: "center" }}
        >
          £{arg.ngnValue.salePrice} per month
        </td>
      </TableRow>
      {mainOption.additionalService.isSelected &&
        renderAdditionalService(services, 0)}
    </React.Fragment>
  );
};

const validateOtherProductFields = (otherProducts, products) => {
  for (const product of products) {
    if (product === "Energy") {
      if (
        !otherProducts.energy.electricity.isSelected &&
        !otherProducts.energy.gas.isSelected
      ) {
        handleOpenSnackBar(
          "error",
          "Please select at least one option for Energy (Electricity or Gas)",
        );
        return false;
      }

      if (otherProducts.energy.electricity.isSelected) {
        const electricityFields = otherProducts.energy.electricity;
        const missingFields = validateRequiredFields(electricityFields);
        if (missingFields.length > 0) {
          handleOpenSnackBar(
            "error",
            `Please fill in the following fields for Electricity: `,
          );
          return false;
        }
      }
      if (otherProducts.energy.gas.isSelected) {
        const gasFields = otherProducts.energy.gas;
        const missingFields = validateRequiredFields(gasFields);
        if (missingFields.length > 0) {
          handleOpenSnackBar(
            "error",
            `Please fill in the following fields for Gas: `,
          );
          return false;
        }
      }
    } else if (product === "Telecoms") {
      if (
        !otherProducts.telecoms.business.isSelected &&
        !otherProducts.telecoms.residential.isSelected
      ) {
        handleOpenSnackBar(
          "error",
          "Please select at least one option for Telecoms (Business or Residential)",
        );
        return false;
      }

      if (otherProducts.telecoms.business.isSelected) {
        const businessFields = otherProducts.telecoms.business;
        const missingFields = validateRequiredFields(businessFields);
        if (missingFields.length > 0) {
          handleOpenSnackBar(
            "error",
            `Please fill in the following fields for Business Telecoms: `,
          );
          return false;
        }
      }
      if (otherProducts.telecoms.residential.isSelected) {
        const residentialFields = otherProducts.telecoms.residential;
        const missingFields = validateRequiredFields(residentialFields);
        if (missingFields.length > 0) {
          handleOpenSnackBar(
            "error",
            `Please fill in the following fields for Residential Telecoms: `,
          );
          return false;
        }
      }
    } else if (product === "Card Processing") {
      const cardFields = otherProducts.cardProcessing;
      const missingFields = validateRequiredFields(cardFields, ["meterNumber"]);
      if (missingFields.length > 0) {
        handleOpenSnackBar(
          "error",
          `Please fill in the following fields for Card Processing: `,
        );
        return false;
      }
    } else if (product === "Mobile") {
      const mobileFields = otherProducts.mobile;
      const missingFields = validateRequiredFields(mobileFields);
      if (missingFields.length > 0) {
        handleOpenSnackBar(
          "error",
          `Please fill in the following fields for Mobile: `,
        );
        return false;
      }
    }
  }
  return true;
};

const validateRequiredFields = (fields, excludeFields = []) => {
  const requiredFields = {
    currentSupplier: "Current Supplier",
    date: "Date",
    meterNumber: "Meter Number",
    monthlySpend: "Monthly Spend",
    notesSelection: "Notes",
    followUpDate: "Follow Up Date",
  };

  const missingFields = [];

  Object.entries(requiredFields).forEach(([key, label]) => {
    if (!excludeFields.includes(key)) {
      if (!fields[key]) {
        if (key === "meterNumber") {
          missingFields.push("Please enter meter/mobile number");
        } else {
          missingFields.push(label);
        }
      } else if (
        key === "meterNumber" &&
        (fields[key].toString().length < 9 ||
          fields[key].toString().length > 14)
      ) {
        missingFields.push("Meter/Mobile number must be between 9-14 digits");
      }
    }
  });

  return missingFields;
};

const formatWebNumbers = (price) => {
  const x = price % 1 === 0 ? price : price?.toFixed(2) || 0;
  return isNaN(x) ? 0 : x;
};

function formatToTwoDecimals(value) {
  const num = parseFloat(value);

  if (Number.isNaN(num)) return "";

  // Agar integer hai to usi format me return karo (e.g. 20)
  if (Number.isInteger(num)) return num.toString();

  // Warna exactly 2 decimal tak round karo
  return num.toFixed(2);
}

const formatPhoneForDisplay = (number) => {
  if (!number) return "";
  const raw = number.replace(/\D/g, ""); // just in case
  const match = raw.match(/^(\d{0,5})(\d{0,3})(\d{0,3})$/);
  if (!match) return raw;

  return [match[1], match[2], match[3]].filter(Boolean).join(" ");
};

function checkSpeedAvailability(cliData) {
  let isDownloadSpeed = cliData.some((x) => {
    if (x.digitalTariff?.isSelected) {
      return x?.lineValues?.some((y) => {
        const val = y?.ngnValue?.isDownloadSpeed;
        return val === undefined ? true : val;
      });
    }
    if (x.legacyTariff?.isSelected) {
      return x?.lineValues?.some((y) => {
        const val = y?.legacy?.isDownloadSpeed;
        return val === undefined ? true : val;
      });
    }
    return false;
  });

  let isUploadSpeed = cliData.some((x) => {
    if (x.digitalTariff?.isSelected) {
      return x?.lineValues?.some((y) => {
        const val = y?.ngnValue?.isUploadSpeed;
        return val === undefined ? true : val;
      });
    }
    if (x.legacyTariff?.isSelected) {
      return x?.lineValues?.some((y) => {
        const val = y?.legacy?.isUploadSpeed;
        return val === undefined ? true : val;
      });
    }
    return false;
  });

  return {
    isDownloadSpeed,
    isUploadSpeed,
  };
}

const renderDiscountValidMonthsText = (discountDetails) => {
  if (discountDetails?.validMonths === 0) {
    return " - Free";
  }
  if (discountDetails?.isHalfPrice) {
    return ` - First ${discountDetails?.validMonths} Months Half Price!`;
  }
  return ` - First ${discountDetails?.validMonths} Months Free!`;
};

const checkIncludeDateFairUsage = (cliData, tariffKey, valueKey) => {
  return cliData.some(
    (item) =>
      item[tariffKey]?.isSelected &&
      item.lineValues.some((line) => line[valueKey]?.includeDateFairUsage),
  );
};

function getDataFairUsagePackagesName(cliData = []) {
  let packageName = [];

  cliData.forEach((item) => {
    if (item.legacyTariff?.isSelected) {
      item.lineValues.forEach((line) => {
        if (line?.legacy?.includeDateFairUsage) {
          packageName.push(line.legacy.value);
        }
      });
    }

    if (item.digitalTariff?.isSelected) {
      item.lineValues.forEach((line) => {
        if (line?.ngnValue?.includeDateFairUsage) {
          packageName.push(line.ngnValue.value);
        }
      });
    }
  });

  const removeDuplicates = (arr) => [...new Set(arr)];

  return removeDuplicates(packageName);
}

const calculateEarlyTerminationCharge = (revenue, contractLength) => {
  // Find the selected contract length
  const selectedLength = selectedContractLength(contractLength);
  // Get the value of the selected contract length
  const length = selectedLength?.value || 0;
  // Calculate the early termination charge
  const totalRevenue = revenue || 0;
  // Calculate the early termination charge
  const earlyTerminationCharge = totalRevenue * length;
  // Return the early termination charge
  return formatToTwoDecimals(earlyTerminationCharge);
};

const decodeHtml = (html) => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.documentElement.textContent;
};

const calculateGpChange = (row) => {
  const gpChange = row.totalGrossProfit - row.previousGrossProfit;
  return gpChange;
};

export {
  handleOpenSnackBar,
  encryptPassword,
  decryptPassword,
  handleApiCall,
  getTotalSalePrice,
  getTotalBuyPrice,
  formatObjKey,
  calcultePackageAmount,
  upgradeChargeAmount,
  checkIfPaygExists,
  contractTerm,
  routerTotalPrice,
  calculateRevenue,
  renderAdditionalService,
  renderKeyPairsRows,
  renderAddHocServices,
  calculateUpgradeCharge,
  renderSogeaCharges,
  hasErrorInLineValues,
  convertNaNToZero,
  selectedContractLength,
  isBuyPricingContract,
  getTariffListPrice,
  withContractPricing,
  renderTableDetailsForLegacy,
  renderTableDetailsForNgN,
  getTotalTariffCharges,
  getTotalServiceChargeOnTarrif,
  checkOnlyDigitalTarrifSelected,
  areAllKeysFilled,
  checkPlatformUpradeSelected,
  handleCheckAllDataFill,
  validateOtherProductFields,
  formatWebNumbers,
  renderAdditionalServiceForContractPdf,
  renderKeyPairsRowsForContractPdf,
  formatToTwoDecimals,
  formatPhoneForDisplay,
  checkSpeedAvailability,
  renderDiscountValidMonthsText,
  checkIncludeDateFairUsage,
  getDataFairUsagePackagesName,
  calculateEarlyTerminationCharge,
  decodeHtml,
  calculateGpChange,
};
